/**
 * Browse (§7.2) — the marketplace, and the only surface a guest sees.
 *
 * §7.6 makes the empty case the central design problem rather than an edge case:
 *  - no listings at all routes to bot creation, because the first cohort here
 *    are owners, not renters
 *  - no matches names the filter that excluded the most results and offers to
 *    relax it, never a bare zero-result page
 *
 * Filter layout: one always-visible line (search + sort) over a disclosure. A
 * renter arrives wanting to look, not to fill in twelve fields, and eleven of
 * these narrow a catalogue they have not seen yet. Everything stays in the DOM
 * and stays keyboard-reachable — `<details>` is the whole mechanism, no JS.
 */

import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { EMPTY_STATES, GRANTABLE_CAPABILITIES, PERIOD_UNIT, PLATFORM } from '@pm/shared';

import { useApi } from '../hooks/useApi';
import type { ListingSearchResponse } from '../api/types';
import { Card, Field, Loading, Badge } from '../components/ui';
import { EmptyState } from '../components/EmptyState';
import { ErrorView } from '../components/ErrorView';
import { amountLabel, humanize, interpolate, percent, periodLabel } from '../lib/format';
import { ASKING_LABEL, OWNER_LABEL } from '../lib/vocabulary';
import { routes } from '../api/routes';

type Filters = {
  q: string;
  platform: string;
  min_price: string;
  max_price: string;
  period_unit: string;
  topic: string;
  language: string;
  min_rating: string;
  min_health: string;
  available_from: string;
  available_to: string;
  permission: string;
  sort: string;
};

const BLANK: Filters = {
  q: '',
  platform: '',
  min_price: '',
  max_price: '',
  period_unit: '',
  topic: '',
  language: '',
  min_rating: '',
  min_health: '',
  available_from: '',
  available_to: '',
  permission: '',
  sort: 'relevance',
};

/** Human names for the filter keys the server may report as most-narrowing. */
const FILTER_LABEL: Record<string, string> = {
  q: 'search text',
  platform: 'platform',
  min_price: ASKING_LABEL.toLowerCase(),
  max_price: ASKING_LABEL.toLowerCase(),
  period_unit: 'rental period',
  topic: 'topic',
  language: 'language',
  min_rating: 'rating',
  min_health: 'health score',
  available_from: 'availability',
  available_to: 'availability',
  permission: 'permission granted',
};

/**
 * What `GET /listings` actually returns.
 *
 * The card previously read `bot_name`, `asking` and `platform`; the endpoint
 * sends `name`, `rental_price` + `currency`, and a `platforms` array. Every one
 * of those rendered as blank or an em-dash, so the marketplace shipped cards
 * with no name and no figure on them. Typing the wire shape here and mapping it
 * once is what stops that from being re-invented per component.
 */
interface ListingRow {
  id: string;
  bot_id: string;
  name: string;
  slug: string;
  owner_handle: string;
  platforms: string[] | null;
  rental_price: number;
  currency: string;
  period_unit: string;
  period_count: number;
  granted_permissions: string[];
  rating_avg: number | string;
  rating_count: number;
  health_score: number | string;
  public_excerpt: string | null;
  topics_allow: string[] | null;
}

/** Minor units are a wire format. Users think in whole currency. */
const MINOR_UNIT_EXPONENT = 2;
const toMinorUnits = (major: string): string =>
  major === '' ? '' : String(Math.round(Number(major) * 10 ** MINOR_UNIT_EXPONENT));

function healthTone(score: number): 'ok' | 'warn' | 'bad' {
  return score > 0.9 ? 'ok' : score > 0.7 ? 'warn' : 'bad';
}

function ListingCard({ row }: { row: ListingRow }) {
  const rating = Number(row.rating_avg);
  const health = Number(row.health_score);
  const platforms = row.platforms ?? [];
  // Six identical chips is a wall, not information. Three plus a count reads.
  const shown = row.granted_permissions.slice(0, 3);
  const extra = row.granted_permissions.length - shown.length;

  return (
    <li className="listing-card">
      <div className="listing-card-head">
        <h3 className="listing-name">
          <Link to={`/listings/${row.id}`} className="listing-link">
            {row.name}
          </Link>
        </h3>
        <p className="listing-byline">
          {OWNER_LABEL} @{row.owner_handle}
          {platforms.length ? <> &middot; {platforms.map(humanize).join(', ')}</> : null}
        </p>
      </div>

      {row.public_excerpt ? <p className="listing-excerpt">{row.public_excerpt}</p> : null}

      <p className="listing-asking">
        <strong className="listing-asking-figure">
          {amountLabel({ amount: row.rental_price, currency: row.currency })}
        </strong>
        <span className="listing-asking-period">
          {' '}
          per {periodLabel(row.period_count, row.period_unit)}
        </span>
      </p>

      <p className="listing-meta">
        <Badge tone={healthTone(health)}>health {percent(health, 0)}</Badge>
        <span className="listing-rating">
          {row.rating_count > 0
            ? `${rating.toFixed(1)} out of 5 · ${row.rating_count} review${row.rating_count === 1 ? '' : 's'}`
            : 'No reviews yet'}
        </span>
      </p>

      <ul className="perm-chips">
        {shown.map((p) => (
          <li key={p} className="chip chip-static">
            {p.replace(/_/g, ' ')}
          </li>
        ))}
        {extra > 0 ? <li className="chip chip-static chip-count">+{extra}</li> : null}
      </ul>
    </li>
  );
}

export function Browse() {
  const [filters, setFilters] = useState<Filters>(BLANK);
  const panelId = useId();
  const set = <K extends keyof Filters>(k: K, v: Filters[K]) =>
    setFilters((f) => ({ ...f, [k]: v }));

  const activeKeys = (Object.keys(BLANK) as (keyof Filters)[]).filter(
    (k) => k !== 'sort' && k !== 'q' && filters[k] !== '',
  );
  const anyFilter = activeKeys.length > 0 || filters.q !== '';

  const res = useApi<ListingSearchResponse>(routes.listings.collection, {
    query: {
      ...filters,
      min_price: toMinorUnits(filters.min_price),
      max_price: toMinorUnits(filters.max_price),
      limit: 24,
    },
  });

  const rows = (res.data?.data ?? []) as unknown as ListingRow[];
  const narrowing = res.data?.narrowing_filter ?? null;

  const relaxNarrowing = () => {
    if (!narrowing) return;
    setFilters((f) => ({ ...f, [narrowing.filter as keyof Filters]: '' }));
  };

  return (
    <div className="browse">
      <header className="page-head">
        <h1>Browse personas</h1>
        <p className="page-head-sub">
          Every figure below is the {OWNER_LABEL.toLowerCase()}&rsquo;s own{' '}
          {ASKING_LABEL.toLowerCase()}, arranged directly with them.
        </p>
      </header>

      <form className="browse-controls" onSubmit={(e) => e.preventDefault()} aria-label="Filter listings">
        <div className="browse-bar">
          <div className="browse-search">
            <label className="sr-only" htmlFor="f-q">
              Search personas
            </label>
            <input
              id="f-q"
              className="input"
              type="search"
              placeholder="Search by name, topic or voice"
              value={filters.q}
              onChange={(e) => set('q', e.target.value)}
            />
          </div>

          <label className="sr-only" htmlFor="f-sort">
            Sort by
          </label>
          <select
            id="f-sort"
            className="select browse-sort"
            value={filters.sort}
            onChange={(e) => set('sort', e.target.value)}
          >
            <option value="relevance">Most relevant</option>
            <option value="price_asc">{ASKING_LABEL}, low to high</option>
            <option value="price_desc">{ASKING_LABEL}, high to low</option>
            <option value="rating">Best rated</option>
            <option value="health">Healthiest</option>
            <option value="recent">Recently listed</option>
            <option value="most_rented">Most rented</option>
          </select>
        </div>

        <details className="browse-filters" open={activeKeys.length > 0}>
          <summary className="browse-filters-summary" aria-controls={panelId}>
            <span>Filters</span>
            {activeKeys.length > 0 ? (
              <span className="browse-filters-count">{activeKeys.length}</span>
            ) : null}
          </summary>

          <div className="browse-filters-body" id={panelId}>
            <div className="filter-grid">
              <Field id="f-platform" label="Platform">
                <select
                  id="f-platform"
                  className="select"
                  value={filters.platform}
                  onChange={(e) => set('platform', e.target.value)}
                >
                  <option value="">Any</option>
                  {PLATFORM.map((p) => (
                    <option key={p} value={p}>
                      {humanize(p)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field id="f-period" label="Rental period">
                <select
                  id="f-period"
                  className="select"
                  value={filters.period_unit}
                  onChange={(e) => set('period_unit', e.target.value)}
                >
                  <option value="">Any</option>
                  {PERIOD_UNIT.map((p) => (
                    <option key={p} value={p}>
                      {humanize(p)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field id="f-topic" label="Topic">
                <input
                  id="f-topic"
                  className="input"
                  placeholder="shipping, baking, football"
                  value={filters.topic}
                  onChange={(e) => set('topic', e.target.value)}
                />
              </Field>

              <Field id="f-lang" label="Language">
                <input
                  id="f-lang"
                  className="input"
                  placeholder="en"
                  value={filters.language}
                  onChange={(e) => set('language', e.target.value)}
                />
              </Field>

              {/* Whole currency in, minor units on the wire. §7.5 forbids a bare
                  noun for this figure, so the group is labelled by the shared
                  vocabulary rather than a hand-typed word. */}
              <fieldset className="filter-range">
                <legend>{ASKING_LABEL}</legend>
                <div className="filter-range-inputs">
                  <span className="filter-affix">$</span>
                  <input
                    id="f-min"
                    className="input"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="min"
                    aria-label={`${ASKING_LABEL} from`}
                    value={filters.min_price}
                    onChange={(e) => set('min_price', e.target.value)}
                  />
                  <span className="filter-range-dash" aria-hidden="true">
                    &ndash;
                  </span>
                  <input
                    id="f-max"
                    className="input"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="max"
                    aria-label={`${ASKING_LABEL} to`}
                    value={filters.max_price}
                    onChange={(e) => set('max_price', e.target.value)}
                  />
                </div>
              </fieldset>

              <fieldset className="filter-range">
                <legend>Available between</legend>
                <div className="filter-range-inputs">
                  <input
                    id="f-from"
                    className="input"
                    type="date"
                    aria-label="Available from"
                    value={filters.available_from}
                    onChange={(e) => set('available_from', e.target.value)}
                  />
                  <span className="filter-range-dash" aria-hidden="true">
                    &ndash;
                  </span>
                  <input
                    id="f-to"
                    className="input"
                    type="date"
                    aria-label="Available to"
                    value={filters.available_to}
                    onChange={(e) => set('available_to', e.target.value)}
                  />
                </div>
              </fieldset>

              <Field id="f-rating" label="Minimum rating" hint="Out of 5">
                <input
                  id="f-rating"
                  className="input"
                  type="number"
                  min={0}
                  max={5}
                  step={0.5}
                  placeholder="Any"
                  value={filters.min_rating}
                  onChange={(e) => set('min_rating', e.target.value)}
                />
              </Field>

              <Field id="f-health" label="Minimum health" hint="0 to 1">
                <input
                  id="f-health"
                  className="input"
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  placeholder="Any"
                  value={filters.min_health}
                  onChange={(e) => set('min_health', e.target.value)}
                />
              </Field>

              <Field id="f-perm" label="Grants permission">
                <select
                  id="f-perm"
                  className="select"
                  value={filters.permission}
                  onChange={(e) => set('permission', e.target.value)}
                >
                  <option value="">Any</option>
                  {GRANTABLE_CAPABILITIES.map((c) => (
                    <option key={c} value={c}>
                      {c.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="browse-filters-foot">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setFilters(BLANK)}
                disabled={!anyFilter}
              >
                Clear all
              </button>
            </div>
          </div>
        </details>
      </form>

      {res.initialLoading ? <Loading label="Loading listings" /> : null}
      {res.problem ? <ErrorView problem={res.problem} onRetry={res.reload} /> : null}

      {res.data && rows.length === 0 ? (
        anyFilter ? (
          <Card>
            <EmptyState
              heading={EMPTY_STATES.marketplaceNoMatches.heading}
              body={
                narrowing
                  ? interpolate(EMPTY_STATES.marketplaceNoMatches.bodyTemplate, {
                      filter: FILTER_LABEL[narrowing.filter] ?? narrowing.filter,
                      count: narrowing.excluded_count,
                    })
                  : 'Try clearing a filter.'
              }
              cta={
                narrowing
                  ? { label: EMPTY_STATES.marketplaceNoMatches.cta, onClick: relaxNarrowing }
                  : { label: 'Clear all', onClick: () => setFilters(BLANK) }
              }
            />
          </Card>
        ) : (
          <Card>
            <EmptyState
              heading={EMPTY_STATES.marketplaceNoListings.heading}
              body={EMPTY_STATES.marketplaceNoListings.body}
              cta={{ label: EMPTY_STATES.marketplaceNoListings.cta, to: '/bots/new' }}
            />
          </Card>
        )
      ) : null}

      {rows.length > 0 ? (
        <>
          <p className="browse-count" role="status">
            {rows.length} persona{rows.length === 1 ? '' : 's'}
            {anyFilter ? ' match your filters' : ' listed'}
          </p>
          <ul className="listing-grid">
            {rows.map((row) => (
              <ListingCard key={row.id} row={row} />
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
